/**
 * MY — Name Reservation (M32001) chatflow.
 *
 * SSM 은 Direct Incorporation 전에 **이름 예약 승인**을 먼저 받는 절차를 쓴다.
 * 그 첫 단계를 Preset Studio 에 올린 것이 이 플로우다.
 *
 * 다른 Incorporation 프리셋과 달리 **문서가 없다**(L_PRESET_TASK.DOC_CODES = NULL).
 * 그래서 화면 흐름도 문서 생성으로 가지 않는다:
 *
 *   Step1 Task title → Step2 T-Link 연결(선택) 또는 Skip → 바로 이름 예약 e-Filing
 *
 * Step2 위젯은 Incorporation (Pre) 와 **같은 것**(`incorporation-doc-entity`)을 쓴다 —
 * 담당자가 이미 익숙한 화면이라 따로 배울 게 없다.
 */
import type { ConversationMessage } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/** 확인 단계 안내 — 문서가 없으므로 "Generate" 가 아니라 e-Filing 으로 넘어간다고 알린다. */
const CONFIRM_MESSAGE =
  'Ready. Click **Create Task** to open the Name Reservation e-Filing form.\n\n' +
  'There are no documents to generate for this preset — the reservation is filed directly to SSM.';

export const nameReservationFlow: ChatFlow = {
  id: 'name-reservation',
  taskCodeMatch: ['M32001'],
  categoryMatch: ['name reservation', 'namereservation', 'reserve name'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    return [
      {
        role: 'ai',
        content:
          `Welcome to **${preSet.task.TASK_NAME || 'Name Reservation'}**.\n\n` +
          `Reserve a company name with SSM before incorporating. ` +
          `Once the name is approved you can proceed with Direct Incorporation using the reserved name.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage:
        'Optionally link a **T-Link / incorporation** to pre-fill the proposed name, ' +
        'business codes, clarifications and promoters — or skip and type them in yourself.',
      widget: 'incorporation-doc-entity',
      widgetConfig: {
        targetVarKey: '_tlinkEntity',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage: CONFIRM_MESSAGE,
      widget: null,
      nextStep: null,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    // Step1 — Task title
    if (
      (eventName === 'value-submit' || eventName === 'text-input-submit') &&
      context.currentStepId === 'input-title'
    ) {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { _taskTitle: String(title).trim(), taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    // Step2 — Skip. 연결을 건너뛰면 이름은 e-Filing 화면에서 직접 입력한다.
    if (
      (eventName === 'value-submit' || eventName === 'skip' || eventName === 'entity-skip') &&
      context.currentStepId === 'select-entity'
    ) {
      return {
        messages: [
          {
            role: 'user',
            content: 'Skipped linking. The proposed name will be entered manually.',
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd: '',
          icpCd: '',
          entityName: '',
          company_name: '',
          registrationNo: '',
          tlinkData: null,
          entitySkipped: true,
        },
        nextStepId: 'confirm',
      };
    }

    // Step2 — T-Link 신청서 선택. 회사명을 이름 예약 폼의 제안 이름으로 넘긴다.
    if (eventName === 'setup-entity-autofill' || eventName === 'entity-field-select') {
      let parsed: any = payload;
      if (typeof payload === 'string') {
        try {
          parsed = JSON.parse(payload);
        } catch {
          parsed = { _entityName: payload };
        }
      }
      const icpCd = String(parsed?._icpCd || parsed?.icpCd || parsed?.entityCd || '').trim();
      const entityName = String(
        parsed?._entityName || parsed?.entityName || parsed?.companyName || '',
      ).trim();
      // incorporation 카드 선택만 처리 — officer / address 쪽 entity-field-select 와 구분한다.
      const isIncPick = !!(parsed?._icpCd || parsed?.icpCd);
      if (eventName === 'entity-field-select' && !isIncPick) return {};
      if (!icpCd) return {};
      return {
        messages: [
          {
            role: 'user',
            content: `Linked incorporation: **${entityName || icpCd}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        formUpdates: {
          entityCd: icpCd,
          icpCd,
          entityName,
          company_name: entityName,
        },
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
